import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

import { TipoInfo } from "@shared/Enums/tipo-info.enum";
import { UsuariosRepository } from "@shared/Repositories/usuarios.repository";
import { FileHandlerService } from "@shared/Services/fileHandler.service";

import { RGVSISharedRepository } from "../Repositories/rgvsishared.repository";
import { FormPolicyCategory } from "../Types/Enums/policy-type.enum";
import { DropdownOption } from "../Types/Interfaces/dropdown-option.interface";
import { VehiculeInspectionCertificate } from "../Types/Interfaces/policy-invoice.interface";
import { VIN } from "../Types/Interfaces/vin.interface";

export interface GetCurrentPoliciesParams {
  formPolicyCategory: FormPolicyCategory;
  policyType?: string;
}

@Injectable()
export class RGVSISharedService {
  constructor(
    private rgvsiSharedRepository: RGVSISharedRepository,
    private usuarioRepository: UsuariosRepository,
    private fileHandlerService: FileHandlerService
  ) {}

  public readonly formCategoryMap = {
    [FormPolicyCategory.VIPE]: this.getCurrentUserVIPEPolicies,
    [FormPolicyCategory.GENERAL]: this.getUserPolicies,
    [FormPolicyCategory.VEHICLE]: this.getCurrentUserVehiclePolices,
    [FormPolicyCategory.PENDING_INVOICES]: this.getCurrentUserPendingInvoicePolicies,
  };

  public get isPersonalClient(): Observable<boolean> {
    return this.usuarioRepository.getInfoClienteActual().pipe(
      map((user) => {
        const tipoInfo = user?.metadata?.tipoInfo;
        return tipoInfo === TipoInfo.PersonalClient;
      })
    );
  }

  public get isClient(): Observable<boolean> {
    return this.usuarioRepository.getInfoClienteActual().pipe(
      map((user) => {
        const tipoInfo = user?.metadata?.tipoInfo;
        const clientTipoInfo = [TipoInfo.CorporativeClient, TipoInfo.PersonalClient];
        return clientTipoInfo.includes(tipoInfo);
      })
    );
  }

  public getIdentificationTypes(): Observable<DropdownOption[]> {
    return this.rgvsiSharedRepository.getIdentificationTypes();
  }

  public getCurrencyTypes(): Observable<DropdownOption[]> {
    return this.rgvsiSharedRepository.getCurrencyTypes();
  }

  public getCurrentUserPolicies({
    policyType,
    formPolicyCategory = FormPolicyCategory.GENERAL,
  }: GetCurrentPoliciesParams): Observable<DropdownOption[]> {
    const currentPolicyCategory = formPolicyCategory || FormPolicyCategory.GENERAL;
    return this.formCategoryMap[currentPolicyCategory].bind(this)(policyType);
  }

  public getUserPolicies(policyType?: string): Observable<DropdownOption[]> {
    return this.rgvsiSharedRepository.getCurrentUserPolicies(policyType);
  }

  public getCurrentUserVIPEPolicies(): Observable<DropdownOption[]> {
    return this.rgvsiSharedRepository.getCurrentUserVIPEPolicies();
  }

  public getCurrentUserVehiclePolices(): Observable<DropdownOption[]> {
    return this.rgvsiSharedRepository.getCurrentUserVehiclePolices();
  }

  public getCurrentUserPendingInvoicePolicies(policyType?: string): Observable<DropdownOption[]> {
    return this.rgvsiSharedRepository.getCurrentUserPendingInvoicePolicies(policyType);
  }

  public getMonths(): Observable<DropdownOption[]> {
    return this.rgvsiSharedRepository.getMonths();
  }

  public getYears(): Observable<string[]> {
    return this.rgvsiSharedRepository.getYears();
  }

  public generateVehicleIdentificationNumberReport(vin: string): Observable<void> {
    return this.rgvsiSharedRepository
      .generateVehicleIdentificationNumberReport(vin)
      .pipe(map(this.fileHandlerService.openPdfInNewTab));
  }

  public getVinsByPolicyNumber(policy: string): Observable<VIN[]> {
    return this.rgvsiSharedRepository.getVinsByPolicyNumber(policy);
  }

  public BalanceAgeDetailRangeService(): Observable<any> {
    return this.rgvsiSharedRepository.BalanceAgeDetailRange();
  }

  public BalanceAgeDetailGraphic(Range: number, CurrencyCode: string): Observable<any> {
    return this.rgvsiSharedRepository.BalanceAgeDetailGraphic(Range, CurrencyCode);
  }

  public BalanceAgeDetailGraphicAmount(CurrencyCode: string): Observable<any> {
    return this.rgvsiSharedRepository.BalanceAgeDetailAmount(CurrencyCode);
  }

  public getCurrentUserVehiclePolicies(): Observable<DropdownOption[]> {
    return this.rgvsiSharedRepository.getCurrentUserVehiclePolicies().pipe(map(this.mapVehiculeInspectionCertToDropOptions));
  }

  private mapVehiculeInspectionCertToDropOptions(vehicule: VehiculeInspectionCertificate[]): DropdownOption[] {
    return vehicule.map(
      (veh: VehiculeInspectionCertificate): DropdownOption => ({
        code: veh.policyNumber,
        description: veh.policyNumber,
        type: veh.vehicleType,
      })
    );
  }
}
